import { useEffect, useRef } from 'react'
import { Flex, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { decodeVideoFrame } from '@/features/portal/qrDecode'

export type CameraFailure = 'unsupported' | 'denied' | 'noCamera' | 'failed'

interface QrCameraProps {
  /** `true` — matn qabul qilindi, kamera to'xtaydi */
  onText: (text: string) => boolean
  onFailure: (reason: CameraFailure) => void
}

const SCAN_INTERVAL_MS = 250
const FIRST_FRAME_TIMEOUT_MS = 6000

function cameraFailure(error: unknown): CameraFailure {
  const name = error instanceof DOMException ? error.name : ''
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'noCamera'
  return 'failed'
}

export function QrCamera({ onText, onFailure }: QrCameraProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const onTextRef = useRef(onText)
  const onFailureRef = useRef(onFailure)

  useEffect(() => {
    onTextRef.current = onText
    onFailureRef.current = onFailure
  })

  useEffect(() => {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      onFailureRef.current('unsupported')
      return
    }

    const canvas = document.createElement('canvas')
    let stream: MediaStream | null = null
    let timer: number | undefined
    let stopped = false

    const stop = (): void => {
      stopped = true
      window.clearTimeout(timer)
      stream?.getTracks().forEach((track) => track.stop())
    }

    const fail = (reason: CameraFailure): void => {
      stop()
      onFailureRef.current(reason)
    }

    const scan = async (video: HTMLVideoElement, startedAt: number): Promise<void> => {
      const text = await decodeVideoFrame(video, canvas).catch(() => null)
      if (stopped) return
      if (text !== null && onTextRef.current(text)) {
        stop()
        return
      }
      if (video.videoWidth === 0 && Date.now() - startedAt > FIRST_FRAME_TIMEOUT_MS) {
        fail('failed')
        return
      }
      timer = window.setTimeout(() => void scan(video, startedAt), SCAN_INTERVAL_MS)
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((media) => {
        stream = media
        const video = videoRef.current
        if (stopped || !video) {
          stop()
          return
        }
        video.srcObject = media
        video.play().catch(() => undefined)
        void scan(video, Date.now())
      })
      .catch((error: unknown) => {
        if (!stopped) fail(cameraFailure(error))
      })

    return stop
  }, [])

  return (
    <Flex vertical gap={8} align="center">
      <video
        ref={videoRef}
        muted
        playsInline
        autoPlay
        style={{
          width: '100%',
          maxWidth: 320,
          aspectRatio: '1 / 1',
          objectFit: 'cover',
          borderRadius: 12,
          background: '#000',
        }}
      />
      <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
        {t('card.cameraHint')}
      </Typography.Text>
    </Flex>
  )
}
